/**
 * Single source of truth for all brand colors.
 * When re-skinning the project, change values ONLY in this file.
 * All other files (tailwind.config.ts, themeColors.ts, pdfTokens.ts, chart components) import from here.
 */
export const colors = {
  // Warm palette (PIF brand)
  peach: {
    DEFAULT: '#FAF6EE',
    1: '#F0E9DB',
    2: '#DDD4C2',
    3: '#CAC1AF',
  },
  pif: {
    green: '#265B4E',
    greenLight: '#3A7A6A',
    gold: '#C5A572',
  },

  // Grey scale — each shade has light-mode and dark-mode value
  grey: {
    black:       { light: '#262524', dark: '#FFFFFF' },
    main:        { light: '#3D3B39', dark: '#E8E3D9' },
    hover:       { light: '#74716E', dark: '#CAC1AF' },
    pseudohover: { light: '#5A5855', dark: '#F0E9DB' },
    medium:      { light: '#CAC1AF', dark: '#74716E' },
    plain:       { light: '#F0E9DB', dark: '#3D3B39' },
    light:       { light: '#FAF6EE', dark: '#1A1815' },
    sky:         { light: '#FDFCF9', dark: '#141210' },
    // Fallback color for sheets without an assigned color
    sheetFallback: '#AABCC5',
  },

  white: { light: '#FFFFFF', dark: '#1A1815' },
  black: { light: '#1A1815', dark: '#FAF6EE' },

  // Semantic status colors
  negative: {
    primary:  { light: '#CA325F', dark: '#D94773' },
    hover:    { light: '#D94773', dark: '#CA325F' },
    disable:  '#D8A3BB',
    subtitle: '#E2BDCE',
  },
  positive: {
    primary:  { light: '#265B4E', dark: '#3A7A6A' },
    hover:    { light: '#3A7A6A', dark: '#265B4E' },
    disable:  '#8CB5AB',
    subtitle: '#B9D5CE',
  },

  brand: {
    main:  '#265B4E',
    gold:  '#C5A572',
    cream: '#FAF6EE',
    dark:  '#1A1815',
  },

  // Chart series palettes (recharts + PDF)
  chartSeries: {
    light: {
      primary:   '#265B4E',
      secondary: '#C5A572',
      success:   '#4F8A6D',
      warning:   '#D98B2B',
      danger:    '#CA325F',
      neutral:   '#74716E',
      accent_1:  '#3A7A6A',
      accent_2:  '#D94773',
      accent_3:  '#5A5855',
    },
    dark: {
      primary:   '#3A7A6A',
      secondary: '#C5A572',
      success:   '#6AA287',
      warning:   '#E6A64A',
      danger:    '#D94773',
      neutral:   '#CAC1AF',
      accent_1:  '#265B4E',
      accent_2:  '#CA325F',
      accent_3:  '#74716E',
    },
  },

  // Avatar / account accent colors (unique vivid palette, not part of main brand)
  avatar: ['#FEAD10', '#3362BD', '#43802A', '#FF67ED', '#00EFFE'],

  // Mermaid diagram theme variables
  mermaid: {
    light: {
      background:         '#ffffff',
      mainBkg:            '#ffffff',
      primaryColor:       '#dbeafe',
      primaryTextColor:   '#1f2937',
      primaryBorderColor: '#bfdbfe',
      secondaryColor:     '#f1f5f9',
      secondaryTextColor: '#1f2937',
      tertiaryColor:      '#e2e8f0',
      lineColor:          '#94a3b8',
      noteBkgColor:       '#f8fafc',
      noteTextColor:      '#1f2937',
      actorTextColor:     '#1f2937',
      clusterBkg:         '#f8fafc',
      clusterBorder:      '#cbd5e1',
      edgeLabelBackground:'#e2e8f0',
    },
    dark: {
      background:           '#0e1213',
      mainBkg:              '#0e1213',
      primaryColor:         '#262d30',
      primaryTextColor:     '#f2f6f7',
      primaryBorderColor:   '#4d5a62',
      secondaryColor:       '#1a1f20',
      secondaryTextColor:   '#f2f6f7',
      tertiaryColor:        '#111516',
      lineColor:            '#4d5a62',
      noteBkgColor:         '#111516',
      noteTextColor:        '#f2f6f7',
      actorTextColor:       '#f2f6f7',
      clusterBkg:           '#111516',
      clusterBorder:        '#4d5a62',
      edgeLabelBackground:  '#1a1f20',
      pieSectionTextColor:  '#f2f6f7',
    },
  },

  // Named shadow tokens (used in tailwind.config.ts boxShadow extension)
  shadows: {
    card:     '0 8px 16px 0 rgba(17, 21, 22, 0.16)',
    subtle:   '0 2px 12px rgba(0, 0, 0, 0.04)',
    compose:  '0 4px 24px rgba(0, 0, 0, 0.10)',
    workflow: '0 4px 20px rgba(38, 91, 78, 0.08)',  // rgba of pif.green
  },
} as const;
